import React, { useEffect, useState } from "react";

import LayersIcon from "@mui/icons-material/Layers";

import Box from "@mui/material/Box";

import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";

import { ConvertCmpVolumesToFeatures, GetLayer, JumpToFeatures } from "../map/mapUtil";

import { useMap as useMapContext } from "../contexts/mapContext";
import { useUserAuth } from "../contexts/authContext";

import useMap from "../hooks/useOpenLayersMap";

import OpenLayersInfoCard from "./mapInfoCard";
import MapFullScreen from "./mapFullScreen";
import { MapLayersDropdown } from "./mapLayersDropdown";

const MapPreview = (props) => {
    const MAP_ID = "map-preview";

    const [mapLayersOpen, setMapLayersOpen] = useState(false);
    const [mapLayersAnchorEl, setMapLayersAnchorEl] = useState(null);

    const [plannedOperationsVisible, setPlannedOperationsVisible] = useState(false);
    const [publishedOperationsVisible, setPublishedOperationsVisible] = useState(false);
    const [constraintsVisible, setConstraintsVisible] = useState(false);
    const [alertVolumesVisible, setAlertVolumesVisible] = useState(false);
    const [coaVolumesVisible, setCoaVolumesVisible] = useState(false);

    const { publishedOperations, planningOperations, constraints, alertVolumes, coaVolumes } = useMapContext();
    const { userMapSettings } = useUserAuth();

    const map = useMap(MAP_ID, false);

    // fire all
    useEffect(() => {
        handleMapPreviewLoaded();
    }, [map]);

    // update and jump to selected geometries when they change
    useEffect(() => {
        handleUpdateSelectedGeometries();
    }, [
        map,
        props.selected_planned_flights,
        props.selected_published_flights,
        props.selected_constraints,
        props.selected_alert_volumes,
        props.selected_coa_volumes
    ]);

    // update other geometries when they change
    useEffect(() => {
        handleUpdateGeometriesThatAreNotSelected();
    }, [map, planningOperations, publishedOperations, constraints, alertVolumes, coaVolumes]);

    // function to create vector layers and initialize map layers
    const handleMapPreviewLoaded = () => {
        if (!map) {
            return;
        }

        const layers = [
            {
                layer_name: "planned_flights",
                visible: Array.isArray(props.selected_planned_flights) ? true : false,
                setVisible: setPlannedOperationsVisible
            },
            {
                layer_name: "published_flights",
                visible: Array.isArray(props.selected_published_flights) ? true : false,
                setVisible: setPublishedOperationsVisible
            },
            {
                layer_name: "constraints",
                visible: Array.isArray(props.selected_constraints) ? true : false,
                setVisible: setConstraintsVisible
            },
            {
                layer_name: "alert_volumes",
                visible: Array.isArray(props.selected_alert_volumes) ? true : false,
                setVisible: setAlertVolumesVisible
            },
            {
                layer_name: "coa_volumes",
                visible: Array.isArray(props.selected_coa_volumes) ? true : false,
                setVisible: setCoaVolumesVisible
            }
        ];
        layers.forEach(({ layer_name: layerName, visible, setVisible }) => {
            const source = new VectorSource({
                features: []
            });
            const layer = new VectorLayer({
                source: source,
                properties: { name: layerName },
                visible: visible
            });
            map.addLayer(layer);

            if (visible) {
                setVisible(true);
            }
        });
    };
    // function to update selected geometries and jump to them
    const handleUpdateSelectedGeometries = () => {
        if (!map) {
            return;
        }

        const layers = [
            {
                layer_name: "planned_flights",
                volumes: props.selected_planned_flights
            },
            {
                layer_name: "published_flights",
                volumes: props.selected_published_flights
            },
            {
                layer_name: "constraints",
                volumes: props.selected_constraints
            },
            {
                layer_name: "alert_volumes",
                volumes: props.selected_alert_volumes
            },
            {
                layer_name: "coa_volumes",
                volumes: props.selected_coa_volumes
            }
        ];
        layers.map(({ layer_name: layerName, volumes }) => {
            if (Array.isArray(volumes)) {
                const layer = GetLayer(map, layerName);
                if (!layer) {
                    return;
                }
                const source = layer.getSource();
                if (!source) {
                    return;
                }
                source.clear();

                const features = ConvertCmpVolumesToFeatures(volumes, userMapSettings);
                source.addFeatures(features);
                JumpToFeatures(map, features);
            }
        });
    };
    // function to update the geoemetries that are not initially visible
    const handleUpdateGeometriesThatAreNotSelected = () => {
        if (!map) {
            return;
        }

        const layers = [
            {
                layer_name: "planned_flights",
                volumes: Array.isArray(props.selected_planned_flights) ? undefined : planningOperations
            },
            {
                layer_name: "published_flights",
                volumes: Array.isArray(props.selected_published_flights) ? undefined : publishedOperations
            },
            {
                layer_name: "constraints",
                volumes: Array.isArray(props.selected_constraints) ? undefined : constraints
            },
            {
                layer_name: "alert_volumes",
                volumes: Array.isArray(props.selected_alert_volumes) ? undefined : alertVolumes
            },
            {
                layer_name: "coa_volumes",
                volumes: Array.isArray(props.selected_coa_volumes) ? undefined : coaVolumes
            }
        ];
        layers.map(({ layer_name: layerName, volumes }) => {
            if (Array.isArray(volumes) === false) {
                return;
            }
            const layer = GetLayer(map, layerName);
            if (!layer) {
                return;
            }
            const source = layer.getSource();
            if (source) {
                source.clear();

                const features = ConvertCmpVolumesToFeatures(volumes, userMapSettings);
                source.addFeatures(features);
            }
        });
    };
    const handleToggleLayersButtonClick = (e) => {
        setMapLayersAnchorEl(e.currentTarget);
        setMapLayersOpen((prev) => !prev);
    };
    const handleLayerToggleButtonClick = (layerName, setVisible) => {
        const layer = GetLayer(map, layerName);
        if (layer) {
            const updatedVisibility = !layer.getVisible();
            layer.setVisible(updatedVisibility);
            setVisible(updatedVisibility);
        }
    };
    return (
        <Box>
            <MapFullScreen
                map_id={MAP_ID}
                map={map}
                buttons={[
                    {
                        name: "Toggle Layers",
                        visible: true,
                        icon: <LayersIcon />,
                        onClick: handleToggleLayersButtonClick
                    }
                ]}
            >
                <OpenLayersInfoCard map={map} showButtons={false} />
            </MapFullScreen>
            {mapLayersOpen === true ? (
                <MapLayersDropdown
                    mapLayersOpen={mapLayersOpen}
                    setMapLayersOpen={setMapLayersOpen}
                    mapLayersAnchorEl={mapLayersAnchorEl}
                    sections={[
                        {
                            title: "Volumes",
                            layers: [
                                {
                                    label: "Planning Operations",
                                    checked: plannedOperationsVisible,
                                    handleLayerClick: () => handleLayerToggleButtonClick("planned_flights", setPlannedOperationsVisible),
                                    sublayers: []
                                },
                                {
                                    label: "Operations",
                                    checked: publishedOperationsVisible,
                                    handleLayerClick: () => handleLayerToggleButtonClick("published_flights", setPublishedOperationsVisible),
                                    sublayers: []
                                },
                                {
                                    label: "Constraints",
                                    checked: constraintsVisible,
                                    handleLayerClick: () => handleLayerToggleButtonClick("constraints", setConstraintsVisible),
                                    sublayers: []
                                },
                                {
                                    label: "Alert Volumes",
                                    checked: alertVolumesVisible,
                                    handleLayerClick: () => handleLayerToggleButtonClick("alert_volumes", setAlertVolumesVisible),
                                    sublayers: []
                                },
                                {
                                    label: "COA Volumes",
                                    checked: coaVolumesVisible,
                                    handleLayerClick: () => handleLayerToggleButtonClick("coa_volumes", setCoaVolumesVisible),
                                    sublayers: []
                                }
                            ]
                        }
                    ]}
                />
            ) : (
                <></>
            )}
        </Box>
    );
};

export default MapPreview;
